/* istanbul ignore file */
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MSAL_GUARD_CONFIG, type MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { type AuthenticationResult, type AccountInfo as AzureAccountInfo, InteractionType, type PopupRequest, type RedirectRequest } from '@azure/msal-browser';
import { from, type Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import type { AccountInfo } from './models';

const GRAPH_PROFILE_IMAGE_ENDPOINT = 'https://graph.microsoft.com/v1.0/me/photos/64x64/$value';

@Injectable({
  providedIn: 'root',
})
export class AzureAuthService {
  private readonly msalGuardConfig = inject(MSAL_GUARD_CONFIG) as MsalGuardConfiguration;
  private readonly http = inject(HttpClient);
  public readonly authService = inject(MsalService);

  /**
   *
   * @param image
   * @returns
   */
  public static createImageFromBlob(image: Blob): Observable<string> {
    const promise: Promise<string> = new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.addEventListener('load', (_e) => resolve(fileReader.result as string));
      fileReader.readAsDataURL(image);
    });

    return from(promise);
  }
  /**
   *
   * @param accountInfo
   * @returns
   */
  public static extractDepartmentFromAzureAccountInfo(accountInfo: AzureAccountInfo): string {
    if (accountInfo.name) {
      const splittedName = accountInfo.name.split(' ');

      return splittedName.length < 3 ? '' : (splittedName.at(-1) ?? '');
    }

    return '';
  }
  /**
   *
   */
  public login() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        const ob = this.authService.loginPopup({
          ...this.msalGuardConfig.authRequest,
        } as unknown as PopupRequest);
        ob.subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
        return ob;
      } else {
        const ob = this.authService.loginPopup();
        ob.subscribe((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        });
        return ob;
      }
    } else if (this.msalGuardConfig.authRequest) {
      return this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as unknown as RedirectRequest);
    } else {
      return this.authService.loginRedirect();
    }
  }
  /**
   *
   */
  public logout(): void {
    this.authService.logoutRedirect();
  }
  /**
   *
   * @returns
   */
  public getProfileImage(): Observable<string> {
    return this.http.get(GRAPH_PROFILE_IMAGE_ENDPOINT, { responseType: 'blob' }).pipe(mergeMap((photoBlob) => AzureAuthService.createImageFromBlob(photoBlob)));
  }
  /**
   *
   * @param acc
   */
  public setActiveAccount(acc: AzureAccountInfo): void {
    this.authService.instance.setActiveAccount(acc);
  }
  /**
   *
   * @returns
   */
  public handleAccount(): AccountInfo | undefined {
    let activeAccount = this.authService.instance.getActiveAccount();

    // take first available account -> could be extended by some other logic
    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    if (activeAccount) {
      const department = AzureAuthService.extractDepartmentFromAzureAccountInfo(activeAccount);

      return { ...activeAccount, department };
    }

    return undefined;
  }
  /**
   * determin a certain role
   * @param role
   * @returns
   */
  public hasRole(role: DtisAzureRoles): boolean {
    return this.handleAccount()?.idTokenClaims?.roles?.includes(String(role)) || false;
  }
}

export enum DtisAzureRoles {
  DTIS_ULTRASONIC_SERVICE = 'DTIS_ULTRASONIC_SERVICE',
  DTIS_ADMINISTRATOR = 'DTIS_ADMINISTRATOR',
  DTIS_USER = 'DTIS_USER',
  SUPER_USER = 'SUPER_USER',
  USER_READ_ROLE = 'USER_READ_ROLE',
}
